/* eslint-disable roros/no-raw-html-in-routes */
// reason: pre-existing <button>/<input>/<select> elements that pre-date the LINT-1 (AILLM-3) rule extension. New code must use components/ui/*. Migration tracked separately.
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { usePageHeader } from '@/hooks/usePageHeader';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { VStack, HStack } from '@/components/ui/Stack';
import { useNotificationStore } from '@/stores/notificationStore';
import { useOrgStore } from '@/stores/orgStore';
import { apiFetch } from '@/lib/api';
import { storageKey } from '@/config/app';
import { Loader2, Search, Filter, Check, X, Globe, Undo2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import { LanguagePanel } from '@/routes/roros/settings/panels/LanguagePanel';

interface TranslationRow {
  id: string;
  namespace: string;
  key: string;
  locale: string;
  value: string;
}

interface OverrideRow {
  id: string;
  org_id: string;
  namespace: string;
  key: string;
  locale: string;
  value: string;
}

/** Grouped translation key with values per locale */
interface GroupedTranslation {
  namespace: string;
  key: string;
  locales: Record<string, { id: string; value: string }>;
}

/** Fetch system translations (read-only, for reference) */
async function fetchSystemTranslations(filters: { namespace?: string; search?: string; locale?: string }): Promise<TranslationRow[]> {
  const params = new URLSearchParams();
  if (filters.namespace) params.set('namespace', filters.namespace);
  if (filters.search) params.set('search', filters.search);
  if (filters.locale) params.set('locale', filters.locale);
  const res = await apiFetch(`/api/translations/overrides/system?${params}`);
  const json = await res.json();
  return json.data || [];
}

async function fetchOverrides(orgId: string): Promise<OverrideRow[]> {
  const res = await apiFetch('/api/translations/overrides', {
    headers: { 'X-Organization-Id': orgId },
  });
  const json = await res.json();
  return json.data || [];
}

async function setOverride(orgId: string, namespace: string, key: string, locale: string, value: string) {
  const res = await apiFetch(`/api/translations/overrides/${namespace}/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-Organization-Id': orgId },
    body: JSON.stringify({ locale, value }),
  });
  return res.json();
}

async function removeOverride(orgId: string, namespace: string, key: string, locale: string) {
  const res = await apiFetch(`/api/translations/overrides/${namespace}/${encodeURIComponent(key)}?locale=${locale}`, {
    method: 'DELETE',
    headers: { 'X-Organization-Id': orgId },
  });
  return res.json();
}

function EditableCell({
  systemValue,
  overrideValue,
  onSave,
  onRevert,
}: {
  systemValue: string;
  overrideValue?: string;
  onSave: (v: string) => void;
  onRevert: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const displayValue = overrideValue ?? systemValue;
  const hasOverride = overrideValue !== undefined;
  const [draft, setDraft] = useState(displayValue);

  const handleSave = () => {
    if (draft !== systemValue || hasOverride) {
      onSave(draft);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(displayValue);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-1">
        <div
          className={`flex-1 px-2 py-1 cursor-pointer hover:bg-accent rounded min-h-[32px] text-sm ${hasOverride ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' : ''}`}
          onClick={() => { setDraft(displayValue); setEditing(true); }}
        >
          {displayValue}
        </div>
        {hasOverride && (
          <button
            onClick={onRevert}
            className="p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded"
            title="Revert to system value"
          >
            <Undo2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') handleCancel();
        }}
        className="flex-1 px-2 py-1 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
        autoFocus
      />
      <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded">
        <Check className="w-4 h-4" />
      </button>
      <button onClick={handleCancel} className="p-1 text-muted-foreground hover:bg-accent rounded">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
};

/**
 * TranslationsPanel — content only, used in the System Settings "Translations"
 * tab. The TranslationOverrides page export below wraps this in
 * PageContainer + PageHeader for the legacy /organization/translations route.
 */
export function TranslationsPanel() {
  const queryClient = useQueryClient();
  const { success, error: notifyError } = useNotificationStore();
  const { activeOrgId } = useOrgStore();
  const orgId = activeOrgId || localStorage.getItem(storageKey('active_org_id')) || '';

  const [search, setSearch] = useState('');
  const [nsFilter, setNsFilter] = useState('');
  const [localeFilter, setLocaleFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const searchTimeout = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setDebouncedSearch(val), 300);
  }, []);

  // Fetch system translations — filtering by namespace/search/locale server-side
  const { data: systemTranslations = [], isLoading } = useQuery({
    queryKey: ['translations-system', nsFilter, debouncedSearch, localeFilter],
    queryFn: () => fetchSystemTranslations({
      namespace: nsFilter || undefined,
      search: debouncedSearch || undefined,
      locale: localeFilter || undefined,
    }),
    enabled: !!orgId,
  });

  // Fetch the full list of available locales independently — the dropdown
  // must stay stable regardless of the filter on systemTranslations.
  const { data: allLocales = [] } = useQuery<string[]>({
    queryKey: ['translations-locales'],
    queryFn: async () => {
      const res = await apiFetch('/api/translations/overrides/locales');
      const json = await res.json();
      return (json.data || []) as string[];
    },
    enabled: !!orgId,
  });

  // Fetch org overrides
  const { data: overrides = [] } = useQuery({
    queryKey: ['translations-overrides', orgId],
    queryFn: () => fetchOverrides(orgId),
    enabled: !!orgId,
  });

  // Build override lookup map
  const overrideMap = React.useMemo(() => {
    const map = new Map<string, OverrideRow>();
    for (const o of overrides) {
      map.set(`${o.namespace}::${o.key}::${o.locale}`, o);
    }
    return map;
  }, [overrides]);

  // Group translations by namespace+key for side-by-side display
  const { grouped, availableLocales } = React.useMemo(() => {
    const keyMap = new Map<string, GroupedTranslation>();
    const localeSet = new Set<string>();

    for (const t of systemTranslations) {
      localeSet.add(t.locale);
      const groupKey = `${t.namespace}::${t.key}`;
      let group = keyMap.get(groupKey);
      if (!group) {
        group = { namespace: t.namespace, key: t.key, locales: {} };
        keyMap.set(groupKey, group);
      }
      group.locales[t.locale] = { id: t.id, value: t.value };
    }

    // Sort locales consistently (de before en, etc.)
    const sortedLocales = Array.from(localeSet).sort();

    return {
      grouped: Array.from(keyMap.values()),
      availableLocales: sortedLocales,
    };
  }, [systemTranslations]);

  const saveMutation = useMutation({
    mutationFn: (params: { namespace: string; key: string; locale: string; value: string }) =>
      setOverride(orgId, params.namespace, params.key, params.locale, params.value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['translations-overrides'] });
      success(t('Override saved'));
    },
    onError: (err: any) => notifyError(err?.message || t('Failed to save override')),
  });

  const revertMutation = useMutation({
    mutationFn: (params: { namespace: string; key: string; locale: string }) =>
      removeOverride(orgId, params.namespace, params.key, params.locale),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['translations-overrides'] });
      success(t('Override removed — reverted to system value'));
    },
    onError: (err: any) => notifyError(err?.message || t('Failed to remove override')),
  });

  // Get unique namespaces from loaded translations
  const namespaces = React.useMemo(() => {
    const ns = new Set(systemTranslations.map(t => t.namespace));
    return Array.from(ns).sort();
  }, [systemTranslations]);

  if (!orgId) {
    return (
      <div className="text-center py-12 text-muted-foreground">No organization selected.</div>
    );
  }

  const overrideCount = overrides.length;

  return (
    <div>
      <VStack spacing={4}>
        {/* Default language selector — same component used standalone elsewhere */}
        <LanguagePanel />

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <HStack spacing={4} className="flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search keys or values..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                  data-no-translate
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select
                  value={nsFilter}
                  onChange={(e) => setNsFilter(e.target.value)}
                  className="px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All namespaces</option>
                  {namespaces.map((ns) => (
                    <option key={ns} value={ns}>{ns}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <select
                  value={localeFilter}
                  onChange={(e) => setLocaleFilter(e.target.value)}
                  className="px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All languages</option>
                  {allLocales.map((loc) => (
                    <option key={loc} value={loc}>{LOCALE_LABELS[loc] || loc.toUpperCase()}</option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-muted-foreground">
                {grouped.length} keys · {allLocales.length} locale{allLocales.length !== 1 ? 's' : ''}
              </div>
            </HStack>
          </CardContent>
        </Card>

        {/* Hint */}
        {overrideCount === 0 && (
          <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            Click any value to set an organization-specific override. For example, rename "Project" to "Shipment" for your logistics team.
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : grouped.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-no-translate>
                {debouncedSearch ? 'No translations match your search.' : 'No translations available.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[100px]" data-no-translate>Namespace</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[200px]" data-no-translate>Key</th>
                      {availableLocales.map(locale => (
                        <React.Fragment key={locale}>
                          <th className="text-left px-4 py-3 font-medium text-muted-foreground" data-no-translate>
                            {LOCALE_LABELS[locale] || locale} — System
                          </th>
                          <th className="text-left px-4 py-3 font-medium text-muted-foreground min-w-[200px]" data-no-translate>
                            {LOCALE_LABELS[locale] || locale} — Override
                          </th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((group) => (
                      <tr key={`${group.namespace}::${group.key}`} className="border-b border-border hover:bg-muted/50">
                        <td className="px-4 py-2">
                          <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs font-medium" data-no-translate>
                            {group.namespace}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-muted-foreground" data-no-translate>
                          {group.key}
                        </td>
                        {availableLocales.map(locale => {
                          const localeData = group.locales[locale];
                          const overrideKey = `${group.namespace}::${group.key}::${locale}`;
                          const override = overrideMap.get(overrideKey);

                          if (!localeData) {
                            return (
                              <React.Fragment key={locale}>
                                <td className="px-4 py-2 text-muted-foreground" data-no-translate>—</td>
                                <td className="px-4 py-2 text-muted-foreground" data-no-translate>—</td>
                              </React.Fragment>
                            );
                          }

                          return (
                            <React.Fragment key={locale}>
                              <td className="px-4 py-2 text-muted-foreground" data-no-translate>
                                {localeData.value}
                              </td>
                              <td className="px-4 py-2" data-no-translate>
                                <EditableCell
                                  systemValue={localeData.value}
                                  overrideValue={override?.value}
                                  onSave={(value) =>
                                    saveMutation.mutate({
                                      namespace: group.namespace,
                                      key: group.key,
                                      locale,
                                      value,
                                    })
                                  }
                                  onRevert={() =>
                                    revertMutation.mutate({
                                      namespace: group.namespace,
                                      key: group.key,
                                      locale,
                                    })
                                  }
                                />
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </VStack>
    </div>
  );
}

/** Legacy page wrapper — the /organization/translations route still uses this. */
export function TranslationOverrides() {
  usePageHeader({ title: t('Translation Overrides'), breadcrumbs: [{ label: t('Organization'), path: '/organization' }] });

  return (
    <PageContainer>
      <TranslationsPanel />
    </PageContainer>
  );
}
